"use strict";

/* ============================================================
   THE SIGNATURE — scroll-driven cinematic engine
   - Scrub sections: sticky stage inside a tall pin wrapper;
     scroll progress drives video.currentTime (lerped).
   - Timed overlays: labels/callouts appear inside progress windows.
   - Reveal steps + nav state on plain scroll position.
   Synchronous scroll handler (rAF-independent) for reliability.
   ============================================================ */
(function () {
  var page = document.querySelector(".sig");
  if (!page) return;

  var reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  /* ---------- in-page nav ---------- */
  var nav = page.querySelector(".sig-nav");
  var navLinks = nav ? [].slice.call(nav.querySelectorAll(".sig-nav-links a")) : [];

  /* ---------- scrub sections ---------- */
  var scrubs = [].slice.call(page.querySelectorAll("[data-scrub]")).map(function (pin) {
    var video = pin.querySelector("video");
    var overlays = [].slice.call(pin.querySelectorAll("[data-window]")).map(function (el) {
      var w = el.getAttribute("data-window").split("-");
      return { el: el, from: parseFloat(w[0]), to: parseFloat(w[1]) };
    });
    return {
      pin: pin,
      video: video,
      autoplay: !!(video && video.hasAttribute("data-autoplay")),
      overlays: overlays,
      shade: pin.querySelector(".sig-shade"),
      heroCopy: pin.querySelector(".sig-hero-copy"),
      hint: pin.querySelector(".sig-scroll-hint"),
      target: 0,
      current: 0,
      duration: 0
    };
  });

  scrubs.forEach(function (s) {
    if (!s.video) return;
    s.video.muted = true;
    s.video.setAttribute("playsinline", "");
    if (s.video.readyState >= 1) s.duration = s.video.duration || 0;
    else s.video.addEventListener("loadedmetadata", function () { s.duration = s.video.duration || 0; });
    if (reduced) { s.video.removeAttribute("preload"); }
  });

  function progressOf(pin) {
    var r = pin.getBoundingClientRect();
    var span = r.height - window.innerHeight;
    if (span <= 0) return 0;
    return Math.min(1, Math.max(0, -r.top / span));
  }

  /* ---------- ambient fitting video: play only when visible ---------- */
  var fitVideo = page.querySelector(".sig-fitting video");
  if (fitVideo) { fitVideo.muted = true; fitVideo.setAttribute("playsinline", ""); }

  /* ---------- reveal elements ---------- */
  var reveals = [].slice.call(page.querySelectorAll("[data-lit]"));

  function onScroll() {
    var vh = window.innerHeight;

    if (nav) nav.classList.toggle("scrolled", window.scrollY > vh * 0.6);

    scrubs.forEach(function (s) {
      var p = progressOf(s.pin);
      s.target = p;

      /* hero: fade up from black, land the copy, hide hint */
      if (s.shade) s.shade.style.opacity = String(Math.max(0, 0.94 - p * 3.2));
      if (s.heroCopy) s.heroCopy.classList.toggle("lit", p > 0.06);
      if (s.hint) s.hint.style.opacity = p > 0.75 ? "0" : "1";

      /* timed overlays */
      s.overlays.forEach(function (o) {
        var cls = o.el.classList.contains("sig-step") ? "lit" : "show";
        o.el.classList.toggle(cls, p >= o.from && p <= o.to);
      });

      /* autoplay sections: run the animation on a loop while on screen */
      if (s.autoplay && s.video && !reduced) {
        var pr = s.pin.getBoundingClientRect();
        var onScreen = pr.bottom > 0 && pr.top < vh;
        if (onScreen && s.video.paused) s.video.play().catch(function () {});
        if (!onScreen && !s.video.paused) s.video.pause();
      }
    });

    reveals.forEach(function (el) {
      if (el.getBoundingClientRect().top < vh * 0.86) el.classList.add("lit");
    });

    if (fitVideo && !reduced) {
      var fr = fitVideo.getBoundingClientRect();
      var visible = fr.bottom > 0 && fr.top < vh;
      if (visible && fitVideo.paused) fitVideo.play().catch(function () {});
      if (!visible && !fitVideo.paused) fitVideo.pause();
    }

    /* nav scroll-spy */
    if (navLinks.length) {
      var active = null;
      navLinks.forEach(function (a) {
        var id = a.getAttribute("href");
        if (!id || id.charAt(0) !== "#") return;
        var sec = document.querySelector(id);
        if (sec && sec.getBoundingClientRect().top <= vh * 0.4) active = a;
      });
      navLinks.forEach(function (a) { a.classList.toggle("active", a === active); });
    }
  }

  /* smooth scrub loop: lerp currentTime toward scroll target */
  function tick() {
    scrubs.forEach(function (s) {
      if (!s.video || !s.duration || reduced || s.autoplay) return;
      s.current += (s.target - s.current) * 0.14;
      var t = s.current * s.duration;
      if (Math.abs((s.video.currentTime || 0) - t) > 0.01) {
        try { s.video.currentTime = t; } catch (e) {}
      }
    });
    requestAnimationFrame(tick);
  }

  /* iOS/mobile: videos that are only seeked (never played) may not load or
     paint frames until a user gesture. Prime them all on first interaction:
     a muted play() kicks the loader, then scrub videos pause on a frame. */
  var primed = false;
  function primeVideos() {
    if (primed) return;
    primed = true;
    [].slice.call(page.querySelectorAll("video")).forEach(function (v) {
      v.muted = true;
      var isLoop = v.hasAttribute("loop") || v.hasAttribute("data-autoplay");
      var p = v.play();
      if (p && p.then) {
        p.then(function () { if (!isLoop) v.pause(); }).catch(function () {});
      } else if (!isLoop) { v.pause(); }
    });
    onScroll();
  }
  window.addEventListener("touchstart", primeVideos, { once: true, passive: true });
  window.addEventListener("scroll", primeVideos, { once: true, passive: true });

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onScroll);
  onScroll();
  if (!reduced) requestAnimationFrame(tick);
  else {
    /* reduced motion: static posters, no scrub, autoplay nothing */
    scrubs.forEach(function (s) { if (s.video) s.video.pause(); });
  }

  /* smooth anchor scrolling for the in-page nav */
  navLinks.concat([].slice.call(page.querySelectorAll('a[href^="#"]:not(.sig-nav-links a)'))).forEach(function (a) {
    a.addEventListener("click", function (e) {
      var id = a.getAttribute("href");
      if (!id || id.charAt(0) !== "#") return;
      var sec = document.querySelector(id);
      if (!sec) return;
      e.preventDefault();
      sec.scrollIntoView({ behavior: reduced ? "auto" : "smooth", block: "start" });
    });
  });
})();
